import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Store, Package, ClipboardList, CreditCard, Tags, Star,
  ArrowLeft, Loader2, Trash2, Plus, ShieldCheck, Ban, PoundSterling,
} from "lucide-react";
import api, { imageUrl, money } from "@/lib/api";
import { LogoWord } from "@/components/Logo";
import { StatusBadge } from "@/components/OrderStatus";
import { CatIcon } from "@/lib/categoryIcons";
import { Stars } from "@/components/Stars";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const NAV = [
  { key: "", label: "Overview", icon: LayoutDashboard },
  { key: "customers", label: "Customers", icon: Users },
  { key: "sellers", label: "Sellers", icon: Store },
  { key: "products", label: "Products", icon: Package },
  { key: "orders", label: "Orders", icon: ClipboardList },
  { key: "transactions", label: "Transactions", icon: CreditCard },
  { key: "categories", label: "Categories", icon: Tags },
  { key: "reviews", label: "Reviews", icon: Star },
];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent || "bg-[#1A3C2A]/8 text-[#1A3C2A]"}`}><Icon size={20} /></div>
      <p className="text-2xl font-display font-extrabold text-[#1A3C2A] mt-3">{value}</p>
      <p className="text-xs text-slate-500 font-semibold">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { pathname } = useLocation();
  const tab = pathname.replace(/^\/admin\/?/, "").split("/")[0];
  const [data, setData] = useState({});

  const load = useCallback(async () => {
    const [stats, users, sellers, products, orders, tx, cats, reviews] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/sellers"),
      api.get("/admin/products"), api.get("/admin/orders"), api.get("/admin/transactions"),
      api.get("/categories"), api.get("/admin/reviews"),
    ]);
    setData({ stats: stats.data, users: users.data, sellers: sellers.data, products: products.data,
      orders: orders.data, tx: tx.data, cats: cats.data, reviews: reviews.data });
  }, []);
  useEffect(() => { load().catch(() => toast.error("Failed to load admin data")); }, [load]);

  if (!data.stats) return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#1A3C2A]" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 p-4 sm:p-6 lg:p-8">
        <aside className="lg:col-span-3">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 lg:sticky lg:top-6">
            <Link to="/" className="flex items-center gap-2 text-xs text-slate-500 mb-4 hover:text-[#1A3C2A]"><ArrowLeft size={14} /> Back to marketplace</Link>
            <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-200"><ShieldCheck size={22} className="text-[#1A3C2A]" /><p className="font-display font-bold text-[#1A3C2A]">Admin</p></div>
            <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
              {NAV.map((n) => {
                const active = tab === n.key; const Icon = n.icon;
                return (
                  <Link key={n.key} to={`/admin${n.key ? "/" + n.key : ""}`} data-testid={`admin-tab-${n.key || "overview"}`}
                    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${active ? "bg-[#1A3C2A] text-white" : "text-[#22252A] hover:bg-slate-100"}`}>
                    <Icon size={17} /> {n.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </aside>

        <main className="lg:col-span-9 space-y-6 pb-20 lg:pb-0">
          {tab === "" && <Overview stats={data.stats} orders={data.orders} />}
          {tab === "customers" && <Customers users={data.users} />}
          {tab === "sellers" && <Sellers sellers={data.sellers} reload={load} />}
          {tab === "products" && <ProductsAdmin products={data.products} reload={load} />}
          {tab === "orders" && <OrdersAdmin orders={data.orders} />}
          {tab === "transactions" && <Transactions tx={data.tx} />}
          {tab === "categories" && <Categories cats={data.cats} reload={load} />}
          {tab === "reviews" && <ReviewsAdmin reviews={data.reviews} reload={load} />}
        </main>
      </div>
    </div>
  );
}

function Overview({ stats, orders }) {
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Marketplace overview</h1>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <Stat icon={PoundSterling} label="Revenue (paid)" value={money(stats.revenue)} accent="bg-[#E07A5F]/10 text-[#E07A5F]" />
        <Stat icon={Users} label="Customers" value={stats.customers} />
        <Stat icon={Store} label="Sellers" value={stats.sellers} />
        <Stat icon={Package} label="Products" value={stats.products} />
        <Stat icon={ClipboardList} label="Orders" value={stats.orders} />
        <Stat icon={ShieldCheck} label="Pending sellers" value={stats.pending_sellers} accent="bg-amber-100 text-amber-600" />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <h2 className="font-display font-bold text-[#1A3C2A] mb-3">Recent orders</h2>
        <div className="space-y-2">
          {orders.slice(0, 8).map((o) => (
            <div key={o.id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
              <span className="font-semibold">{o.order_number}</span>
              <span className="text-slate-500 hidden sm:block">{o.seller_name}</span>
              <span className="font-bold text-[#1A3C2A]">{money(o.total)}</span>
              <StatusBadge status={o.status} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Table({ head, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{head.map((h) => <th key={h} className="text-left font-semibold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Customers({ users }) {
  const customers = users.filter((u) => u.role === "customer");
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Customers ({customers.length})</h1>
      <Table head={["Name", "Email", "Area", "Joined"]}>
        {customers.map((u) => (
          <tr key={u.id} data-testid={`admin-customer-${u.id}`} className="border-t border-slate-100">
            <td className="px-4 py-3 font-semibold">{u.name}</td>
            <td className="px-4 py-3 text-slate-500">{u.email}</td>
            <td className="px-4 py-3 text-slate-500">{u.area || "—"}</td>
            <td className="px-4 py-3 text-slate-500">{new Date(u.created_at).toLocaleDateString("en-GB")}</td>
          </tr>
        ))}
      </Table>
    </>
  );
}

function Sellers({ sellers, reload }) {
  const setStatus = async (s, status) => {
    try { await api.put(`/admin/sellers/${s.id}/status`, { status }); toast.success(`Seller ${status}`); reload(); }
    catch { toast.error("Failed"); }
  };
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Sellers ({sellers.length})</h1>
      <div className="space-y-3">
        {sellers.map((s) => (
          <div key={s.id} data-testid={`admin-seller-${s.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3">
            <img src={imageUrl(s.logo)} alt="" className="w-12 h-12 rounded-xl object-cover bg-slate-100" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-[#1A3C2A] truncate">{s.shop_name}</p>
              <p className="text-xs text-slate-500">{s.category} · {s.area} · {s.product_count} products</p>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.status === "active" ? "bg-green-100 text-green-700" : s.status === "suspended" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{s.status}</span>
            {s.status !== "active" ? (
              <Button data-testid={`approve-seller-${s.id}`} onClick={() => setStatus(s, "active")} className="rounded-full bg-[#1A3C2A] hover:bg-[#132E20] h-9 gap-1"><ShieldCheck size={15} /> Approve</Button>
            ) : (
              <Button data-testid={`suspend-seller-${s.id}`} onClick={() => setStatus(s, "suspended")} variant="outline" className="rounded-full border-red-200 text-red-600 h-9 gap-1"><Ban size={15} /> Suspend</Button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function ProductsAdmin({ products, reload }) {
  const del = async (p) => {
    if (!window.confirm(`Remove "${p.name}"?`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Listing removed"); reload(); } catch { toast.error("Failed"); }
  };
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Products ({products.length})</h1>
      <Table head={["Product", "Seller", "Price", "Stock", ""]}>
        {products.map((p) => (
          <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-t border-slate-100">
            <td className="px-4 py-3 font-semibold flex items-center gap-2"><img src={imageUrl((p.images || [])[0])} alt="" className="w-8 h-8 rounded-lg object-cover bg-slate-100" /> {p.name}</td>
            <td className="px-4 py-3 text-slate-500">{p.seller_name}</td>
            <td className="px-4 py-3">{money(p.price)}</td>
            <td className="px-4 py-3 text-slate-500">{p.stock}</td>
            <td className="px-4 py-3"><button data-testid={`admin-delete-product-${p.id}`} onClick={() => del(p)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button></td>
          </tr>
        ))}
      </Table>
    </>
  );
}

function OrdersAdmin({ orders }) {
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Orders ({orders.length})</h1>
      <Table head={["Order", "Customer", "Seller", "Total", "Payment", "Status"]}>
        {orders.map((o) => (
          <tr key={o.id} data-testid={`admin-order-${o.id}`} className="border-t border-slate-100">
            <td className="px-4 py-3 font-semibold">{o.order_number}</td>
            <td className="px-4 py-3 text-slate-500">{o.customer_name}</td>
            <td className="px-4 py-3 text-slate-500">{o.seller_name}</td>
            <td className="px-4 py-3 font-bold text-[#1A3C2A]">{money(o.total)}</td>
            <td className="px-4 py-3"><span className={`text-xs font-semibold ${o.payment_status === "paid" ? "text-green-600" : "text-red-500"}`}>{o.payment_status}</span></td>
            <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
          </tr>
        ))}
      </Table>
    </>
  );
}

function Transactions({ tx }) {
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Transactions ({tx.length})</h1>
      <Table head={["Session", "Amount", "Status", "Date"]}>
        {tx.map((t) => (
          <tr key={t.id} className="border-t border-slate-100">
            <td className="px-4 py-3 font-mono text-xs text-slate-500">{(t.session_id || "").slice(0, 24)}…</td>
            <td className="px-4 py-3 font-bold text-[#1A3C2A]">{money(t.amount)}</td>
            <td className="px-4 py-3"><span className={`text-xs font-semibold ${t.payment_status === "paid" ? "text-green-600" : "text-amber-600"}`}>{t.payment_status}</span></td>
            <td className="px-4 py-3 text-slate-500">{new Date(t.created_at).toLocaleString("en-GB")}</td>
          </tr>
        ))}
      </Table>
    </>
  );
}

function Categories({ cats, reload }) {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/categories", { name: name.trim(), icon: "Store" }); toast.success("Category added"); setName(""); reload(); }
    catch { toast.error("Failed"); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Deleted"); reload(); } catch { toast.error("Failed"); }
  };
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Categories</h1>
      <div className="flex gap-2">
        <Input data-testid="new-category-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" className="rounded-xl" />
        <Button data-testid="add-category-button" onClick={add} className="rounded-xl bg-[#1A3C2A] hover:bg-[#132E20] gap-1"><Plus size={16} /> Add</Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cats.map((c) => (
          <div key={c.id} data-testid={`admin-category-${c.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between">
            <span className="flex items-center gap-2 font-semibold text-[#22252A]"><CatIcon name={c.icon} size={16} /> {c.name}</span>
            <button data-testid={`delete-category-${c.id}`} onClick={() => del(c)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </>
  );
}

function ReviewsAdmin({ reviews, reload }) {
  const del = async (r) => {
    try { await api.delete(`/admin/reviews/${r.id}`); toast.success("Review removed"); reload(); } catch { toast.error("Failed"); }
  };
  return (
    <>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A]">Reviews ({reviews.length})</h1>
      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2"><span className="font-semibold text-[#22252A]">{r.user_name}</span><Stars rating={r.rating} showNumber={false} /></div>
              {r.comment && <p className="text-sm text-slate-500 mt-1">{r.comment}</p>}
            </div>
            <button data-testid={`delete-review-${r.id}`} onClick={() => del(r)} className="p-2 rounded-lg hover:bg-red-50 text-red-500 shrink-0"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </>
  );
}
